import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, X } from 'lucide-react';

/**
 * Banner global de atualização do PWA.
 *
 * Como funciona:
 *  - `vite-plugin-pwa` está configurado com `registerType: 'autoUpdate'`,
 *    `skipWaiting: true` e `clientsClaim: true`. Isso faz o service worker
 *    novo ser instalado e ativado assim que detectado.
 *  - Porém, a página em execução continua servindo o JS antigo até o
 *    usuário recarregar manualmente. Em PWAs, isso causa o problema
 *    clássico de "deployei mas o usuário não vê a mudança".
 *  - O `useRegisterSW` dispara `needRefresh=true` exatamente quando o SW
 *    novo está pronto. Aqui mostramos um banner persistente no canto
 *    inferior direito convidando o usuário a recarregar. Um clique em
 *    "Atualizar" chama `updateServiceWorker(true)`, que faz reload
 *    forçado já com a versão nova.
 *  - O usuário pode dispensar com o "X" — útil se estiver no meio de algo
 *    importante e não quiser perder estado. Nesse caso o app continua
 *    funcionando com a versão antiga até a próxima recarga manual.
 */
export const PwaUpdateBanner = () => {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl) {
      // Log mínimo pra confirmar registro em produção
      console.info('[PWA] Service worker registrado:', swUrl);
    },
    onRegisterError(error) {
      console.error('[PWA] Falha ao registrar service worker:', error);
    },
  });

  if (!needRefresh) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 max-w-sm animate-fade-in"
    >
      <div className="flex items-start gap-3 p-4 bg-primary text-on-primary rounded-xl shadow-2xl border border-on-primary/10">
        <RefreshCw size={20} className="shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Nova versão disponível</p>
          <p className="text-xs opacity-90 mt-0.5">
            Clique em "Atualizar" pra carregar a versão mais recente do DRAtento.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => updateServiceWorker(true)}
              className="px-3 py-1.5 bg-on-primary text-primary rounded-lg text-xs font-bold hover:bg-on-primary/90 transition-colors"
            >
              Atualizar agora
            </button>
            <button
              type="button"
              onClick={() => setNeedRefresh(false)}
              className="px-3 py-1.5 text-xs font-medium opacity-80 hover:opacity-100 transition-opacity"
            >
              Depois
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          aria-label="Dispensar"
          className="p-1 -m-1 opacity-70 hover:opacity-100 transition-opacity"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
